import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { 
  loginUser, 
  registerUser, 
  getCurrentUser, 
  signOut as cognitoSignOut,
  changePassword as cognitoChangePassword,
  respondToAuthChallenge as respondToAuthChallengeCognito,
  resendSignUp
} from '../services/cognitoService';

// Define types
interface User {
  username: string;
  email: string;
  fullName: string;
  phoneNumber: string;
  deviceId?: string;
  attributes?: Record<string, string>;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: (username: string, password: string) => Promise<any>;
  register: (username: string, password: string, email: string, phoneNumber: string, fullName: string, deviceId: string, onRegisterSuccess: (verificationSent: boolean) => void) => Promise<void>;
  signOut: () => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  error: string | null;
  completeNewPasswordChallenge: (username: string, newPassword: string, session: string) => Promise<void>;
  respondToAuthChallenge: (username: string, newPassword: string, session: string, userAttributes?: Record<string, string>) => Promise<any>;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Check if user is already logged in
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (token) {
          setAccessToken(token);
          const userResponse = await getCurrentUser(token);
          
          if (userResponse && userResponse.Username) {
            // Convert user attributes to a more usable format
            const attributes: Record<string, string> = {};
            userResponse.UserAttributes?.forEach(attr => {
              if (attr.Name && attr.Value) {
                attributes[attr.Name] = attr.Value;
              }
            });

            setUser({
              username: userResponse.Username,
              email: attributes.email || '',
              fullName: attributes.name || '',
              phoneNumber: attributes.phone_number || '',
              deviceId: attributes['preferred_username'],
              attributes
            });
            setIsAuthenticated(true);
          }
        }
      } catch (err) {
        console.error('Error checking authentication status:', err);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Login function
  const login = async (username: string, password: string) => {
    console.log('Login attempt for:', username);
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await loginUser(username, password);
      console.log('Login response:', response);
      
      if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('New password required challenge');
        return response;
      }
      
      if (response?.AuthenticationResult?.AccessToken) {
        console.log('Login successful, got access token');
        const token = response.AuthenticationResult.AccessToken;
        localStorage.setItem('accessToken', token);
        if (response.AuthenticationResult.RefreshToken) {
          localStorage.setItem('refreshToken', response.AuthenticationResult.RefreshToken);
        }
        setAccessToken(token);
        
        const userResponse = await getCurrentUser(token);
        console.log('Current user response:', userResponse);
        
        if (userResponse?.Username) {
          const attributes: Record<string, string> = {};
          userResponse.UserAttributes?.forEach(attr => {
            if (attr.Name && attr.Value) {
              attributes[attr.Name] = attr.Value;
            }
          });

          setUser({
            username: userResponse.Username,
            email: attributes.email || '',
            fullName: attributes.name || '',
            phoneNumber: attributes.phone_number || '',
            deviceId: attributes['preferred_username'],
            attributes
          });
          setIsAuthenticated(true);
        }
        return response;
      }
      throw new Error('Login failed: No authentication result returned');
    } catch (err: any) {
      console.error('Login error in AuthContext:', err);
      setError(err.message || 'An error occurred during login');
      throw err; // Re-throw to allow Login component to handle specific errors
    } finally {
      setIsLoading(false);
    }
  };

  // Register function
  const register = async (
    username: string, 
    password: string, 
    email: string, 
    phoneNumber: string,
    fullName: string,
    deviceId: string,
    onRegisterSuccess: (verificationSent: boolean) => void
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if user is already registered but not verified
      const verificationSent = sessionStorage.getItem(`verification_sent_${username}`);
      
      // Only register if not already sent verification
      if (!verificationSent) {
        await registerUser(
          username, 
          password, 
          email, 
          phoneNumber,
          fullName,
          deviceId
        );
        
        // Mark that we've sent verification
        sessionStorage.setItem(`verification_sent_${username}`, 'true');
        
        // Indicate that verification was just sent
        onRegisterSuccess(true);
      } else {
        // Verification already sent, don't resend
        onRegisterSuccess(false);
      }
    } catch (err: any) {
      // If user already exists but is unconfirmed, still consider it a success
      if (err.name === 'UsernameExistsException') {
        sessionStorage.setItem(`verification_sent_${username}`, 'true');
        onRegisterSuccess(true);
        return;
      }
      
      setError(err.message || 'An error occurred during registration');
      console.error('Registration error:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Sign out function
  const signOut = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (accessToken) {
        await cognitoSignOut(accessToken);
      }
    } catch (err: any) {
      console.error('Sign out error:', err);
    } finally {
      // Regardless of API success/failure, clear local state
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      setAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  };

  // Change password function
  const changePassword = async (oldPassword: string, newPassword: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (!accessToken) {
        throw new Error('Not authenticated');
      }
      
      await cognitoChangePassword(accessToken, oldPassword, newPassword);
    } catch (err: any) {
      setError(err.message || 'An error occurred while changing password');
      console.error('Change password error:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Function to complete the new password challenge
  const completeNewPasswordChallenge = async (username: string, newPassword: string, session: string) => {
    try {
      // Here you would typically call a function from your cognitoService
      // that handles the NEW_PASSWORD_REQUIRED challenge
      // For example:
      // const response = await respondToAuthChallenge(username, newPassword, session);
      
      // After successfully completing the challenge, log in with the new password
      await login(username, newPassword);
    } catch (err: any) {
      setError(err.message || 'Failed to set new password');
      console.error('Complete new password error:', err);
      throw err;
    }
  };

  // Function to respond to auth challenge
  const respondToAuthChallenge = async (username: string, newPassword: string, session: string, userAttributes?: Record<string, string>): Promise<any> => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Call the cognitoService to respond to the auth challenge
      const response = await respondToAuthChallengeCognito(username, newPassword, session, userAttributes);
      
      // If we get here, the challenge was successful
      if (response.AuthenticationResult) {
        const { AccessToken, IdToken, RefreshToken } = response.AuthenticationResult;
        
        if (!AccessToken || !IdToken) {
          throw new Error('Authentication failed: Missing required tokens');
        }
        
        localStorage.setItem('accessToken', AccessToken);
        localStorage.setItem('idToken', IdToken);
        if (RefreshToken) {
          localStorage.setItem('refreshToken', RefreshToken);
        }
        
        // Get user details and update state
        const userData = await getCurrentUser(AccessToken);
        if (!userData.Username) {
          throw new Error('User data is incomplete');
        }
        
        const user: User = {
          username: userData.Username,
          email: userData.UserAttributes?.find(attr => attr.Name === 'email')?.Value || '',
          fullName: userData.UserAttributes?.find(attr => attr.Name === 'name')?.Value || '',
          phoneNumber: userData.UserAttributes?.find(attr => attr.Name === 'phone_number')?.Value || '',
          deviceId: userData.UserAttributes?.find(attr => attr.Name === 'preferred_username')?.Value,
          attributes: userData.UserAttributes?.reduce((acc: Record<string, string>, attr) => {
            if (attr.Name && attr.Value) {
              acc[attr.Name] = attr.Value;
            }
            return acc;
          }, {})
        };
        
        setUser(user);
        setIsAuthenticated(true);
      }
      
      return response;
    } catch (err) {
      console.error('Error responding to auth challenge:', err);
      setError(err instanceof Error ? err.message : 'Failed to respond to authentication challenge');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    register,
    signOut,
    changePassword,
    completeNewPasswordChallenge,
    respondToAuthChallenge,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
